/**
 * BucketButtons3D — 3D floating bucket buttons rendered inside the Three.js scene.
 *
 * Renders rounded-rectangle planes arranged in a horizontal arc below the particle
 * shape. Each button shows the bucket label + count via Html from @react-three/drei.
 * Clicking a button selects/deselects a bucket filter. The active button's world
 * position is exported via `activeBucketPositionRef` so BucketFilterLinker can
 * originate lines from it.
 */

import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import type { Bucket } from '@arkturian/wave-engine/data/DataIntelligenceController';

/** Maximum number of 3D buttons visible at once (top by count). */
const MAX_VISIBLE_BUTTONS = 12;

/** Shared ref: world position of the currently active bucket button. */
export const activeBucketPositionRef = { current: null as THREE.Vector3 | null };

// ─── Rounded Plane Geometry ──────────────────────────────────────────────────

function createRoundedPlaneGeometry(width: number, height: number, radius: number): THREE.ShapeGeometry {
    const shape = new THREE.Shape();
    const x = -width / 2;
    const y = -height / 2;
    const r = Math.min(radius, width / 2, height / 2);
    shape.moveTo(x + r, y);
    shape.lineTo(x + width - r, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + r);
    shape.lineTo(x + width, y + height - r);
    shape.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    shape.lineTo(x + r, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - r);
    shape.lineTo(x, y + r);
    shape.quadraticCurveTo(x, y, x + r, y);
    return new THREE.ShapeGeometry(shape, 8);
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface BucketButtons3DProps {
    buckets: Bucket[];
    activeBucket: string | null;
    onSelect: (bucket: Bucket | null) => void;
    shapeSize: number;
}

// ─── Single Button ───────────────────────────────────────────────────────────

interface ButtonProps {
    bucket: Bucket;
    isActive: boolean;
    position: [number, number, number];
    onSelect: (bucket: Bucket | null) => void;
    geometry: THREE.ShapeGeometry;
}

const ACTIVE_COLOR = new THREE.Color(0xea580c);
const DEFAULT_COLOR = new THREE.Color(0x1e293b);
const HOVER_COLOR = new THREE.Color(0x334155);

const BucketButton3D: React.FC<ButtonProps> = ({ bucket, isActive, position, onSelect, geometry }) => {
    const meshRef = useRef<THREE.Mesh>(null!);
    const materialRef = useRef<THREE.MeshBasicMaterial>(null!);
    const [hovered, setHovered] = useState(false);

    // Update active bucket world position ref
    useFrame(() => {
        if (isActive && meshRef.current) {
            if (!activeBucketPositionRef.current) {
                activeBucketPositionRef.current = new THREE.Vector3();
            }
            meshRef.current.getWorldPosition(activeBucketPositionRef.current);
        }
    });

    // Animate material color + opacity
    useFrame((_, delta) => {
        const mat = materialRef.current;
        if (!mat) return;

        const targetColor = isActive ? ACTIVE_COLOR : hovered ? HOVER_COLOR : DEFAULT_COLOR;
        const targetOpacity = isActive ? 1.0 : hovered ? 0.85 : 0.7;
        const speed = 6.0;

        mat.color.lerp(targetColor, Math.min(1, delta * speed));
        mat.opacity += (targetOpacity - mat.opacity) * Math.min(1, delta * speed);
    });

    const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        onSelect(isActive ? null : bucket);
    }, [bucket, isActive, onSelect]);

    const handlePointerOver = useCallback((e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
    }, []);

    const handlePointerOut = useCallback(() => {
        setHovered(false);
        document.body.style.cursor = 'auto';
    }, []);

    return (
        <group position={position}>
            <mesh
                ref={meshRef}
                geometry={geometry}
                onPointerDown={handleClick}
                onPointerOver={handlePointerOver}
                onPointerOut={handlePointerOut}
            >
                <meshBasicMaterial
                    ref={materialRef}
                    color={isActive ? ACTIVE_COLOR : DEFAULT_COLOR}
                    transparent
                    opacity={isActive ? 1.0 : 0.7}
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh>

            <Html
                center
                distanceFactor={8}
                style={{
                    pointerEvents: 'none',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                }}
            >
                <div style={{
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    fontWeight: isActive ? 700 : 400,
                    color: '#f1f5f9',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                }}>
                    <div>{bucket.label}</div>
                    <div style={{ fontSize: '8px', opacity: 0.6 }}>{bucket.count}</div>
                </div>
            </Html>
        </group>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BucketButtons3D: React.FC<BucketButtons3DProps> = ({ buckets, activeBucket, onSelect, shapeSize }) => {
    // Clear position ref when no bucket is active
    useEffect(() => {
        if (!activeBucket) {
            activeBucketPositionRef.current = null;
        }
    }, [activeBucket]);

    // Sort by count descending, take top N
    const visibleBuckets = useMemo(() => {
        if (buckets.length <= MAX_VISIBLE_BUTTONS) return buckets;
        return [...buckets]
            .sort((a, b) => b.count - a.count)
            .slice(0, MAX_VISIBLE_BUTTONS);
    }, [buckets]);

    // Button dimensions scale with shape size
    const buttonWidth = Math.max(0.6, shapeSize * 0.25);
    const buttonHeight = buttonWidth * 0.4;
    const cornerRadius = buttonHeight * 0.25;

    // Shared geometry for all buttons (same dimensions)
    const geometry = useMemo(
        () => createRoundedPlaneGeometry(buttonWidth, buttonHeight, cornerRadius),
        [buttonWidth, buttonHeight, cornerRadius],
    );

    // Compute positions: horizontal arc below the shape
    const positions = useMemo(() => {
        const count = visibleBuckets.length;
        if (count === 0) return [];

        const yPos = -shapeSize * 1.5;
        const gap = buttonWidth * 1.15;
        const totalWidth = (count - 1) * gap;
        const startX = -totalWidth / 2;

        return visibleBuckets.map((_, i) => {
            const x = startX + i * gap;
            // Slight arc: lower in the center, higher on edges
            const t = count > 1 ? (i / (count - 1)) * 2 - 1 : 0; // -1 to 1
            const yArc = -Math.abs(t) * shapeSize * 0.05;
            return [x, yPos + yArc, 0] as [number, number, number];
        });
    }, [visibleBuckets, shapeSize, buttonWidth]);

    if (visibleBuckets.length === 0) return null;

    return (
        <group>
            {visibleBuckets.map((bucket, i) => (
                <BucketButton3D
                    key={bucket.label}
                    bucket={bucket}
                    isActive={activeBucket === bucket.label}
                    position={positions[i]}
                    onSelect={onSelect}
                    geometry={geometry}
                />
            ))}
        </group>
    );
};

export default BucketButtons3D;
